import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Loader2, CheckCircle2, Clock, ArrowRight,
  Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck,
  MessageCircle, RotateCcw,
} from 'lucide-react';
import { ordersApi } from '@/services/api';

const STEP_ICONS = [Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck];
const STEP_KEYS = ['measurements', 'techVisit', 'showroom', 'agreement', 'production', 'installation'];

// Maps current_step → the page the user should continue on
const CONTINUE_ROUTES = {
  1: '/schedule-visit',
  2: '/schedule-visit',
  3: '/showroom',
  4: '/agreement',
  5: '/production',
  6: '/installation',
};

const STATUS_CLASSES = {
  active:    'bg-gold/15 text-gold border border-gold/30',
  completed: 'bg-green-500/15 text-green-400 border border-green-500/30',
  cancelled: 'bg-red-500/15 text-red-400 border border-red-500/30',
};

const FollowUpPage = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const isAr = currentLang === 'ar';

  const [form, setForm] = useState({ orderNumber: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = isAr
      ? 'تابع طلبك | أرمان كيتشنز'
      : 'Follow Up Your Order | Arman Kitchens';
  }, [isAr]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!form.orderNumber.trim() || !form.password.trim()) return;
    setLoading(true);
    setNotFound(false);
    setOrder(null);
    try {
      const res = await ordersApi.lookupByPassword(form.orderNumber.trim(), form.password.trim());
      const found = res.data;
      // Save to localStorage so order journey pages recognize the user
      localStorage.setItem('arman_order', JSON.stringify({
        orderId: found.id,
        orderNumber: found.order_number,
        customerName: found.customer_name,
        customerPhone: found.customer_phone,
        accessPassword: found.access_password,
      }));
      setOrder(found);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (!order) return;
    const route = CONTINUE_ROUTES[order.current_step] || '/schedule-visit';
    navigate(route);
  };

  const handleReset = () => {
    setOrder(null);
    setNotFound(false);
    setForm({ orderNumber: '', password: '' });
  };

  const continueRoute = order ? (CONTINUE_ROUTES[order.current_step] || '/schedule-visit') : '/';
  const isCompleted = order?.status === 'completed';

  const waLink = order
    ? `https://wa.me/?text=${encodeURIComponent(
        isAr
          ? `استفسار عن طلبي — ${order.order_number}`
          : `Inquiry about my order — ${order.order_number}`
      )}`
    : '#';

  return (
    <main className="pt-24 min-h-screen">

      {/* ── Hero ── */}
      <section className="relative bg-brand-dark text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-8 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 60%, var(--brand-orange) 0%, transparent 45%), radial-gradient(circle at 80% 40%, var(--brand-orange) 0%, transparent 45%)',
          }}
        />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="container-custom relative z-10 py-20 md:py-28 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <span className="label-text mb-4 block">
              {isAr ? 'خدمة متابعة الطلبات' : 'Order Tracking'}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
              {isAr ? 'تابع طلبك' : 'Follow Up Your Order'}
            </h1>
            <p className="text-white/55 text-lg max-w-xl mx-auto">
              {isAr
                ? 'أدخل رقم طلبك وكلمة المرور لمتابعة حالة مطبخك والاستمرار في إتمام طلبك'
                : 'Enter your order number and password to track your kitchen status and continue your order'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="section-padding bg-warm-white">
        <div className="container-custom max-w-2xl">
          <AnimatePresence mode="wait">

            {/* ── Lookup Form ── */}
            {!order && (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <div className="glass p-8 md:p-12 shadow-xl">
                  <div className="flex justify-center mb-8">
                    <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center border border-gold/20">
                      <Search size={36} className="text-gold" />
                    </div>
                  </div>

                  <form onSubmit={handleSearch} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-charcoal/70 mb-2">
                        {isAr ? 'رقم الطلب' : 'Order Number'}
                        <span className="text-gold ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        dir="ltr"
                        value={form.orderNumber}
                        onChange={(e) => setForm((p) => ({ ...p, orderNumber: e.target.value }))}
                        placeholder="AK-2026-0001"
                        className="w-full px-5 py-4 border-2 border-charcoal/12 bg-white text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-gold transition-colors text-base font-mono tracking-wide"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-charcoal/70 mb-2">
                        {isAr ? 'كلمة المرور' : 'Access Password'}
                        <span className="text-gold ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        dir="ltr"
                        value={form.password}
                        onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                        placeholder="123456"
                        maxLength={6}
                        inputMode="numeric"
                        className="w-full px-5 py-4 border-2 border-charcoal/12 bg-white text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-gold transition-colors text-base font-mono tracking-widest"
                      />
                      <p className="text-charcoal/40 text-xs mt-2">
                        {isAr
                          ? 'الرمز المكوّن من 6 أرقام من رسالة تأكيد طلبك'
                          : 'The 6-digit code from your order confirmation message'}
                      </p>
                    </div>

                    {notFound && (
                      <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-3"
                      >
                        <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <p className="text-red-600 text-sm font-medium">
                          {isAr
                            ? 'لم يتم العثور على الطلب. تأكد من رقم الطلب وكلمة المرور.'
                            : 'Order not found. Please check your order number and password.'}
                        </p>
                      </motion.div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !form.orderNumber || !form.password}
                      className="w-full bg-gold text-charcoal-deep font-bold py-5 flex items-center justify-center gap-3 text-lg hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                      {loading ? (
                        <><Loader2 size={20} className="animate-spin" /> {isAr ? 'جاري البحث...' : 'Searching...'}</>
                      ) : (
                        <><Search size={20} /> {isAr ? 'تابع طلبك' : 'Track My Order'}</>
                      )}
                    </button>
                  </form>

                  <div className="mt-8 pt-6 border-t border-charcoal/8 text-center">
                    <p className="text-charcoal/45 text-sm mb-3">
                      {isAr ? 'ليس لديك طلب؟' : "Don't have an order yet?"}
                    </p>
                    <Link
                      to="/start"
                      className="inline-flex items-center gap-2 text-gold font-semibold hover:underline"
                    >
                      {isAr ? 'ابدأ طلبك الآن' : 'Start a New Order'}
                      <ArrowRight size={16} className={isAr ? 'rotate-180' : ''} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Order Result ── */}
            {order && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className="space-y-5"
              >
                {/* Order Header */}
                <div className="glass p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="label-text mb-1">{isAr ? 'رقم الطلب' : 'Order Number'}</p>
                      <p className="text-3xl font-bold text-charcoal tracking-wide" dir="ltr">
                        {order.order_number}
                      </p>
                      <p className="text-charcoal/55 mt-1">{order.customer_name}</p>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-3">
                      <span className={`text-xs font-bold px-3 py-1.5 ${STATUS_CLASSES[order.status] || STATUS_CLASSES.active}`}>
                        {order.status === 'active'
                          ? (isAr ? 'نشط' : 'Active')
                          : order.status === 'completed'
                          ? (isAr ? 'مكتمل' : 'Completed')
                          : (isAr ? 'ملغي' : 'Cancelled')}
                      </span>
                      <p className="text-charcoal/40 text-sm">
                        {isAr ? `الخطوة ${order.current_step} من 6` : `Step ${order.current_step} of 6`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Continue Banner — only if order is still active */}
                {!isCompleted && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15 }}
                    className="relative overflow-hidden border-2 border-gold/30 bg-gradient-to-br from-gold/8 to-gold/15 p-6"
                  >
                    <div className="absolute -top-6 -end-6 w-28 h-28 rounded-full bg-gold/10 pointer-events-none" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-bold text-charcoal text-lg mb-1">
                          {isAr ? 'طلبك في انتظارك!' : 'Your order is waiting!'}
                        </p>
                        <p className="text-charcoal/60 text-sm">
                          {isAr
                            ? `الخطوة التالية: ${t(`order.steps.${STEP_KEYS[(order.current_step ?? 1) - 1]}.title`)}`
                            : `Next step: ${t(`order.steps.${STEP_KEYS[(order.current_step ?? 1) - 1]}.title`)}`}
                        </p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        animate={{
                          boxShadow: [
                            '0 0 0 0 rgba(239,127,26,0)',
                            '0 0 0 10px rgba(239,127,26,0.18)',
                            '0 0 0 0 rgba(239,127,26,0)',
                          ],
                        }}
                        transition={{ repeat: Infinity, duration: 2.2 }}
                        onClick={handleContinue}
                        className="flex-shrink-0 flex items-center gap-3 bg-gold text-charcoal-deep font-bold px-8 py-4 hover:opacity-90 transition-opacity"
                      >
                        {isAr ? 'أكمل طلبك' : 'Continue My Order'}
                        <ArrowRight size={18} className={isAr ? 'rotate-180' : ''} />
                      </motion.button>
                    </div>
                  </motion.div>
                )}

                {/* Timeline */}
                <div className="glass p-6 md:p-8">
                  <p className="label-text mb-7">{isAr ? 'مراحل الطلب' : 'Order Timeline'}</p>
                  <div>
                    {STEP_KEYS.map((key, index) => {
                      const stepNum = index + 1;
                      const Icon = STEP_ICONS[index];
                      const isStepCompleted = stepNum < order.current_step;
                      const isStepActive = stepNum === order.current_step;

                      return (
                        <div key={key} className="flex items-start gap-5">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isStepCompleted
                                ? 'bg-gold'
                                : isStepActive
                                ? 'bg-gold/10 border-2 border-gold'
                                : 'bg-charcoal/5 border border-charcoal/10'
                            }`}>
                              {isStepCompleted
                                ? <CheckCircle2 size={18} className="text-white" />
                                : <Icon size={15} className={isStepActive ? 'text-gold' : 'text-charcoal/25'} />
                              }
                            </div>
                            {index < STEP_KEYS.length - 1 && (
                              <div className={`w-0.5 h-12 ${isStepCompleted ? 'bg-gold' : 'bg-charcoal/8'}`} />
                            )}
                          </div>

                          <div className="flex-1 pt-1.5 pb-8">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className={`font-semibold ${
                                  isStepActive ? 'text-charcoal' : isStepCompleted ? 'text-charcoal/55' : 'text-charcoal/25'
                                }`}>
                                  {t(`order.steps.${key}.title`)}
                                </p>
                                <p className={`text-sm mt-0.5 ${isStepActive ? 'text-charcoal/55' : 'text-charcoal/25'}`}>
                                  {t(`order.steps.${key}.desc`)}
                                </p>
                                {key === 'techVisit' && order.technician_visit?.date && (
                                  <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                                    <Clock size={11} /> {order.technician_visit.date} — {order.technician_visit.time_slot}
                                  </p>
                                )}
                                {key === 'showroom' && order.showroom_visit?.date && (
                                  <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                                    <Clock size={11} /> {order.showroom_visit.date} — {order.showroom_visit.time_slot}
                                  </p>
                                )}
                                {key === 'production' && order.production?.status && order.production.status !== 'pending' && (
                                  <p className="text-xs text-gold mt-1">{t(`order.production.${order.production.status}`)}</p>
                                )}
                                {key === 'agreement' && order.agreement?.total_price && (
                                  <p className="text-xs text-charcoal/50 mt-1">
                                    {order.agreement.total_price.toLocaleString()} {t('order.currency')}
                                  </p>
                                )}
                                {key === 'installation' && order.installation?.date && (
                                  <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                                    <Clock size={11} /> {order.installation.date}
                                  </p>
                                )}
                              </div>

                              {isStepCompleted && (
                                <span className="text-xs text-green-600 font-semibold flex-shrink-0">
                                  {isAr ? 'مكتمل' : 'Done'}
                                </span>
                              )}
                              {isStepActive && (
                                <span className="text-xs text-gold font-semibold flex-shrink-0 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse inline-block" />
                                  {isAr ? 'جاري' : 'In Progress'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Admin notes */}
                {order.admin_notes && (
                  <div className="bg-gold/5 border border-gold/20 p-5">
                    <p className="text-xs font-semibold text-gold/70 uppercase tracking-wider mb-1">
                      {isAr ? 'ملاحظة من الفريق' : 'Note from the Team'}
                    </p>
                    <p className="text-charcoal text-sm leading-relaxed">{order.admin_notes}</p>
                  </div>
                )}

                {/* Bottom Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {!isCompleted && (
                    <button
                      onClick={handleContinue}
                      className="flex-1 bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity"
                    >
                      {isAr ? 'أكمل طلبك' : 'Continue My Order'}
                      <ArrowRight size={18} className={isAr ? 'rotate-180' : ''} />
                    </button>
                  )}
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 border-2 border-[#25D366]/40 text-charcoal font-bold py-4 flex items-center justify-center gap-3 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all"
                  >
                    <MessageCircle size={18} className="text-[#25D366]" />
                    {isAr ? 'تواصل معنا' : 'Contact Us on WhatsApp'}
                  </a>
                  <button
                    onClick={handleReset}
                    className="sm:w-14 flex items-center justify-center gap-2 px-4 py-4 border-2 border-charcoal/12 text-charcoal/50 hover:border-charcoal/25 hover:text-charcoal transition-all"
                    title={isAr ? 'بحث جديد' : 'New Search'}
                  >
                    <RotateCcw size={16} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
};

export default FollowUpPage;
